// typescript
import {DiscussionWithoutContentDTO, GroupDiscussionDTO} from "@/generated-client";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { ShortPostReplyBox } from "@/main/react/shortpost/ShortPostReplyBox";
import {Localizer} from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import React from "react";
import {Eye, Loader2, UserRound, Users} from "lucide-react";
import {FOLLOWED_USERS_SOURCE_TYPE, type FeedCardSourceFields} from "@/main/common/FeedCardTypes";
import DiscussionButtons from "@/main/react/discussion/DiscussionButtons";
import BoardCardBlockLazy from "@/main/react/board/BoardCardBlockLazy";
import { useBoardCardSummary } from "@/main/react/board/boardCardSummaries";
import {MetaHandler} from "@/main/common/MetaHandler";
// App locale, not the browser's: `readCount.toLocaleString()` grouped by the OS, so a member reading
// in German from an English browser saw `1,234` — which reads as one-point-two-three-four in German.
// Review F1, 2026-08-25.
import {formatNumber} from "@/main/util/LocalizationUtil";
import {getDisplayUsername, getUserProfilePath} from "@/main/common/UsernameDisplayUtils";
import { getSensitivePreferenceMode, resolveSensitiveMaskingConfig, type SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import {type ImageCarouselRenderabilityState} from "@/main/react/image/ImageCarouselDisplayer";
import {CardTitle} from "@/components/ui/card";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import {RESPONSIVE_CARD_IMAGE_SIZES, SMALL_IMAGE_SIZE, THUMBNAIL_IMAGE_SIZE} from "@/main/util/ImageUrlHelper";
import SafeImage from "@/main/util/SafeImage";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { GroupMemberFlairChip } from "@/main/react/group/GroupMemberFlairChip";
import {navigateToUrl} from "@/main/common/BrowserNavigation";
import {onFeedCardActivated, type FeedCardAttribution} from "@/main/analytics/feedAttribution";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import {type WithDiscussionAuthorAvatar} from "@/main/common/DiscussionAuthorAvatar";
import DiscussionCardMediaCarousel, {
    type DiscussionCardMediaItem,
} from "@/main/react/card/DiscussionCardMediaCarousel";
import {parseDiscussionCardMediaItems} from "@/main/react/card/DiscussionCardMedia";
import {
    FEED_CARD_MEDIA_ASPECT_PROPERTY,
    FEED_CARD_MEDIA_BOX_ASPECT_CLASS,
    FEED_CARD_MEDIA_BOX_CLASS,
    FEED_CARD_MEDIA_SLIDE_CLASS,
    resolveFeedCardMediaBoxAspectRatio,
} from "@/main/react/card/feedCardMediaBox";
import { buildSeriesDiscussionHref } from "@/main/react/series/SeriesReadingTypes";
import { ReadingTimeLabel, hasReadingTime } from "@/main/react/discussion/ReadingTimeLabel";
import type { PinnedGroupDiscussionLike } from "@/main/react/group/GroupGovernanceTypes";

function isGroupDiscussion(d: DiscussionWithoutContentDTO | GroupDiscussionDTO): d is GroupDiscussionDTO {
    return (d as GroupDiscussionDTO).groupPublicId !== undefined;
}

function createInitialDiscussionMediaRenderabilityState(items: readonly DiscussionCardMediaItem[]): ImageCarouselRenderabilityState | null {
    if (items.length === 0) {
        return null;
    }

    // `clip` counts alongside `iframe`, and the omission was not cosmetic: this state is what the
    // parent's own effect writes AFTER the child's `onRenderabilityChange` has already reported (child
    // effects run first), and the child never notifies again because its memoized state never changes.
    // So the parent's initial value is the one that stands, and a clip-only card sat behind the
    // loading overlay for the life of the mount. `DiscussionCardMediaCarousel.initialSlideStatus` has
    // always treated both kinds as pre-loaded; this is the half that had drifted. The `iframe` case of
    // exactly this bug already has a regression test — "does not leave iframe-only discussion cards
    // stuck behind the loading overlay" — and `clip` was its untested twin (2026-08-23).
    const loadedImages = items.filter((item) => item.kind === "iframe" || item.kind === "clip").length;

    return {
        totalImages: items.length,
        loadedImages,
        failedImages: 0,
        hasRenderableImages: loadedImages > 0,
        allImagesFailed: false,
        isResolving: loadedImages === 0,
    };
}

/**
 * The feed card's ROOT class string, hand-mirrored by `ssr_home_first_card.jte` for the card the server
 * paints before React mounts. Exported as a named constant for the same reason
 * `AUTHOR_ATTRIBUTION_ROW_CLASS` is: `feedCardBoxParity.spec.ts` reads the value from here and asserts
 * the JTE carries it, so a one-sided edit goes red instead of shipping two different cards.
 *
 * It had no such pin until 2026-08-03, and the gap was not theoretical — the parity spec covered the
 * media box and the attribution row, so the cards refine run's radius change (`rounded-2xl
 * border-border/60` -> the `Card` primitive's own `rounded-xl border border-border`) could have landed
 * in React alone and left the first home card at a different radius from every card beneath it, with no
 * guard going red. Change this string and the JTE together, or don't change it.
 *
 * It carries no `shadow-*` at rest and none on `focus-within` either. Both used to be spelled
 * `shadow-[var(--surface-shadow-soft)]`, and the token resolves to `none` in `:root` AND in `.dark`
 * (`css/00-variables.css:61,217` — the dark half since the 2026-08-04 SV-73 pass), so removing them on
 * 2026-08-09 changed no rendered pixel. What it removes is the standing invitation to give that token a
 * value again, which would re-elevate ~40 resting surfaces at once. Elevation here is the hairline
 * border, and the hover affordance is `card-hover`'s background wash (`css/01-base.css:102`).
 */
/*
 * SV-135 S2 (2026-09-23, design `2026-09-21-quiet-chrome-feed-visual-weight-design.md` §5): the feed
 * item is a ROW, not a box. No radius, no box border, no card fill and no `overflow-hidden` — one
 * `--divider` edge under each row (1.2-1.8:1, pinned by `borderTokenContrast.spec.ts`), with the hover
 * wash from `card-hover`. Clipping moved to the media box (`.feed-card-media-box`, S8). The root is a
 * plain `div data-slot="card"` rather than the `Card` primitive, because the primitive's base
 * `rounded-xl border bg-card` would merge back in through `cn()`; `data-slot="card"` stays because
 * browser specs address feed cards by it.
 */
export const FEED_CARD_ROOT_CLASS =
    "relative min-w-0 w-full border-b border-divider card-hover";

/**
 * The row's two-column grid: a 40px avatar gutter the whole row aligns to, then the text column
 * (author line, title, excerpt, media, actions). Mirrored verbatim by `ssr_home_first_card.jte`;
 * `feedCardBoxParity.spec.ts` pins both constants against it.
 */
export const FEED_ROW_GRID_CLASS = "grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-3 px-4 py-3";
/**
 * The compact short-post row (SV-141 D5): the same grid with a tighter BOTTOM. The top stays `pt-3`, the
 * SSR first card's, so a short post painted by `ssr_home_first_card.jte` does not move its author line or
 * text when the island row replaces it (rule 32); only the row's bottom edge, under the actions, tightens.
 */
export const FEED_ROW_GRID_COMPACT_CLASS = "grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-3 px-4 pt-3 pb-2";
/**
 * The row's action row (SV-135 D2, design §5.2): in the text column, left-aligned (`-ml-2` so the first
 * ghost control's icon sits on the text edge), capped at 28rem, with `DiscussionButtons`' `layout="row"`
 * spreading its controls across it. The skeleton twins carry it verbatim
 * (`flatXRestingSurfaces.contract.test.tsx`). Only the HEIGHT is mirrored by `ssr_home_first_card.jte`.
 */
export const FEED_ROW_ACTIONS_CLASS = "mt-1 -ml-2 flex min-w-0 max-w-[28rem] items-center justify-between [&>div]:flex-nowrap [&>div]:gap-1 sm:[&>div]:gap-2 md:[&>div]:gap-3";
/**
 * The `…` menu's slot at the end of the author line (D2). Fixed at the author line's 20px (`h-5`), so the
 * 32px trigger (44px under a coarse pointer) overflows it instead of growing the line: the SSR first card
 * draws no menu, and a line that grew on mount would shift every row below it (rule 32). Raised so the
 * trigger outranks the whole-row link.
 */
const FEED_ROW_MENU_SLOT_CLASS = "relative z-layer-raised-front ml-auto flex h-5 w-8 shrink-0 items-center justify-center";
/** The gutter avatar's box: 40px (`size-10`), top-aligned, outranking the whole-row link. */
export const FEED_ROW_GUTTER_AVATAR_CLASS = "relative z-layer-raised-front block size-10 shrink-0 self-start";

type DiscussionCardDisplayerProps = {
    discussion: DiscussionWithoutContentDTO | GroupDiscussionDTO;
    currentGroupPinnedDiscussion?: PinnedGroupDiscussionLike;
    viewerIsLeaderOfGroup?: boolean;
    contextGroupPublicId?: string;
    contextSeriesPublicId?: string;
    seriesSequenceNumber?: number;
    seriesSequenceCue?: string;
    onGroupPinnedDiscussionAdded?: (discussion: PinnedGroupDiscussionLike) => void;
    onGroupPinnedDiscussionRemoved?: (discussionPublicId?: string, pinId?: number | null) => void;
    sensitiveMasking?: SensitiveMaskingDirective;
    /**
     * Marks this card's media image as the high-priority LCP candidate. Set only for the first
     * above-the-fold card in a feed. See
     * designs/01-now/2026-06-11-homepage-cwv-remaining-structural-slices-design.md (Slice A).
     */
    priorityImage?: boolean;
    /**
     * Set by a feed list to make this card's activation measurable: it emits `feed_card_clicked` and
     * records the click origin that becomes `discussion_views.source` on the discussion page. Absent
     * on rails, dialogs, galleries and anywhere else that is not a feed, which leaves the card
     * bit-for-bit unchanged. See
     * designs/01-now/2026-07-30-feed-discovery-instrumentation-design.md.
     */
    feedAttribution?: FeedCardAttribution;
    /**
     * `false` drops the avatar and username from the card, keeping the timestamps.
     *
     * Set by a list whose ROUTE already asserts the author — "discussions I authored" repeats the
     * viewer's own avatar and handle on every row, which is the one fact the heading above the list
     * has already stated. The timestamps stay because "when did I post this" is what the row is
     * actually for; only the identity goes.
     *
     * Deliberately a prop rather than a comparison against the signed-in user inside the card: a card
     * cannot know whether the SURFACE is a self-list, and "the author happens to be me" is a
     * different question from "this list is mine" (your own post in a group feed still wants its
     * byline). The three self-list cards — this one, SeriesCardDisplayer and ThoughtCardDisplayer —
     * share this prop name on purpose; it is one contract decided once. Design doc §9.3.
     */
    showAuthor?: boolean;
    /**
     * `true` appends this post's read count to the metadata line. Set ONLY by a list of the viewer's
     * own posts.
     *
     * The value itself is already on the DTO (`viewsCount`) for every viewer of every list, so this
     * prop decides DISPLAY and nothing else — which is exactly the product decision it implements:
     * read counts are not shown publicly, and never have been (the wattpad gap analysis records it as
     * a standing decision). A card that is not told to show one shows none, so every existing call
     * site stays correct without being touched, and the public feed cannot acquire a read count by
     * accident.
     *
     * ⚠️ Three Jest specs assert the count never reaches `DiscussionButtons`
     * (`DiscussionCardDisplayer.bookmarks.spec.tsx`, `DiscussionContentDisplayer.spec.tsx`,
     * `DiscussionPage.spec.tsx`). If a change here makes one of them fail, the count has landed in a
     * shared control instead of this metadata line — that failure is the guard, not a stale test.
     *
     * Deliberately a boolean rather than a number, matching `showAuthor` directly above: a card cannot
     * know whether the SURFACE is a self-list, and that is the only question this answers. Design:
     * designs/01-now/2026-08-19-author-readership-aggregate-and-per-post-reads-design.md §6.
     */
    showReadCount?: boolean;
    /**
     * `false` hides the three controls that record a reaction the VIEWER produced — upvote,
     * downvote and save — plus follow, and keeps share, comments and edit. Operator decision
     * 2026-08-20 (wave-6 `DECISIONS.md` §4 D9, `DASHBOARD-25`): on your own dashboard those
     * controls invite you to react to yourself.
     *
     * Like `showReadCount` and `showAuthor` above, deliberately a boolean about the SURFACE — a
     * card cannot know it is in a self-list. Defaults to `true` in `DiscussionButtons` itself, so
     * omitting it anywhere is today's behaviour exactly.
     */
    showViewerReactions?: boolean;
    /**
     * This card is on the viewer's OWN saved list. Forwarded verbatim to `DiscussionButtons`, whose
     * prop doc owns the reasoning and the two rows it carries (`EMPTY-STATES-81`, `EMPTY-STATES-100`).
     *
     * Same shape as the three booleans above and for the same reason: a card cannot know which SURFACE
     * it is on, so the host says so. Defaults off in `DiscussionButtons` itself, so every existing call
     * site is unchanged.
     */
    viewerSavedList?: boolean;
    /**
     * This card is in the "Boards to vote on" lane (BOARD-17, settle r42 D16). A board card there draws a
     * "Skip" beside the open item's vote controls, and once the server records the pass the card leaves
     * the lane in place. Same shape as the surface booleans above: a card cannot know which lane it is in,
     * so the host says so. Off by default, so every other feed is unchanged.
     */
    boardLaneSkippable?: boolean;
    /**
     * `GROUP-78` (S7 U26): how the card's media sits on a PHONE. `"hero"` (the default, every host but
     * one) is today's full-bleed media block at every width. `"thumbnail"` is the group feed's scanning
     * unit: below `sm`, a titled card whose first slide has a paintable preview draws a 64px thumbnail
     * beside its title and hides the full-bleed block; from `sm` up nothing changes.
     *
     * <p>A decision about ONE feed, not about card imagery: the image is useful on a post page and on
     * the home feed, and a visitor on a group page is scanning titles to decide whether to join (~478
     * CSS px per card, ~1.75 cards per phone screen, before this). Falls back to the hero when the post
     * is sensitive or a spoiler (the guard below owns masking and the thumbnail sits outside it),
     * title-less, or has no preview to paint (an embed without a poster).
     */
    phoneMediaLayout?: "hero" | "thumbnail";
    localizer: Localizer;
};

/**
 * Props equal for rendering purposes: shallow, except `feedAttribution`, compared by value. Feed lists
 * build the attribution inline per card, so without this the memo below would never hold.
 */
export function discussionCardPropsAreEqual(
    previous: DiscussionCardDisplayerProps,
    next: DiscussionCardDisplayerProps,
): boolean {
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]) as Set<keyof DiscussionCardDisplayerProps>;
    for (const key of keys) {
        if (key === "feedAttribution") {
            const a = previous.feedAttribution;
            const b = next.feedAttribution;
            if (a === b) {
                continue;
            }
            if (!a || !b || a.feedType !== b.feedType || a.ordering !== b.ordering
                || a.position !== b.position || a.recommendationSource !== b.recommendationSource) {
                return false;
            }
            continue;
        }
        if (!Object.is(previous[key], next[key])) {
            return false;
        }
    }
    return true;
}

/**
 * Memoized because feed lists render dozens of these and re-run their render function on every
 * pagination state flip (`loading` on, then off): without the memo every card already loaded
 * re-rendered twice per page fetch, a cost that grew with scroll depth (2026-09-23 React render review).
 * Viewer state still reaches the card through its stores and records, which a memo does not block.
 */
export const DiscussionCardDisplayer = React.memo(DiscussionCardDisplayerInner, discussionCardPropsAreEqual);

function DiscussionCardDisplayerInner(props: DiscussionCardDisplayerProps) {
    const {
        discussion,
        currentGroupPinnedDiscussion,
        viewerIsLeaderOfGroup = false,
        contextGroupPublicId,
        contextSeriesPublicId,
        seriesSequenceNumber,
        seriesSequenceCue,
        onGroupPinnedDiscussionAdded,
        onGroupPinnedDiscussionRemoved,
        sensitiveMasking,
        priorityImage = false,
        feedAttribution,
        showAuthor = true,
        showReadCount = false,
        showViewerReactions = true,
        viewerSavedList = false,
        boardLaneSkippable = false,
        phoneMediaLayout = "hero",
        localizer,
    } = props;
    const discussionWithAuthorAvatar = discussion as WithDiscussionAuthorAvatar<typeof discussion>;
    const discussionWithFlags = discussion as (DiscussionWithoutContentDTO | GroupDiscussionDTO) & { sensitive?: boolean | null; spoiler?: boolean | null };
    const discussionWithBookmarksState = discussion as (DiscussionWithoutContentDTO | GroupDiscussionDTO) & { isAddedToBookmarksSeries?: boolean | null };
    const discussionWithLifecycleState = discussion as (DiscussionWithoutContentDTO | GroupDiscussionDTO) & {
        archived?: boolean | null;
        deleted?: boolean | null;
        deletedAt?: string | null;
    };
    const df = React.useMemo(() => new DateFormatter(localizer), [localizer]);
    const discussionWithMedia = discussion as (DiscussionWithoutContentDTO | GroupDiscussionDTO) & {
        cardMediaItems?: string | null;
    };
    const parsedCardMediaItems = React.useMemo(
        () => parseDiscussionCardMediaItems(discussionWithMedia.cardMediaItems),
        [discussionWithMedia.cardMediaItems],
    );
    // The clamped aspect ratio of the FIRST slide, or null when its intrinsic dimensions are unknown —
    // which is permanent for embeds, external images and everything published before user_images
    // carried dimensions. Item [0] only, never per-slide: it is the slide the SSR card reserves for,
    // and resizing the box on carousel advance would be the layout shift this box exists to prevent.
    const mediaBoxAspectRatio = React.useMemo(
        () => resolveFeedCardMediaBoxAspectRatio(parsedCardMediaItems),
        [parsedCardMediaItems],
    );
    const [imageRenderability, setImageRenderability] = React.useState<ImageCarouselRenderabilityState | null>(null);
    /**
     * Does the media carousel actually page (arrows + dots)? REPORTED BY the carousel, never counted
     * here: it pages on `preparedItems`, taken after unrenderable slides are dropped, and a second
     * count in this file is precisely the disagreement `CardMediaPagingContext` refuses.
     *
     * It decides whether the media block outranks this card's own full-card link below. A carousel
     * that cannot page needs no pointer, and taking it anyway is what stopped the card being a link
     * over its image (rule 26 — the click was never dispatched at all). See
     * `designs/01-now/2026-09-01-discussion-card-media-swallows-the-card-link-design.md`.
     */
    const [mediaPages, setMediaPages] = React.useState(false);
    // D2: the author line's menu slot, handed to `DiscussionButtons` as a ref (it resolves the element
    // itself, before paint, so this card does not re-render for it).
    const menuSlotRef = React.useRef<HTMLSpanElement | null>(null);
    const initialMediaRenderabilityState = React.useMemo(
        () => createInitialDiscussionMediaRenderabilityState(parsedCardMediaItems),
        [parsedCardMediaItems],
    );

    React.useEffect(() => {
        setImageRenderability(initialMediaRenderabilityState);
    }, [initialMediaRenderabilityState]);

    const createdAt = (discussion)?.createdAt;
    const updatedAt = (discussion)?.updatedAt;
    const formattedCreated = createdAt ? df.timeSince(createdAt) : "";
    const formattedUpdated = updatedAt ? df.timeSince(updatedAt) : "";
    /**
     * A record with no usable title used to render an empty title band AND give the whole-card overlay
     * anchor `aria-label=""` — a link with no accessible name at all, which a screen-reader or
     * voice-control user cannot refer to or tell apart from its neighbours, and which a sighted visitor
     * reads as a card that half-loaded (GROUP-5, 2026-08-14). One value feeds both the visible title and
     * the anchor's name on purpose, so the two cannot drift.
     */
    /**
     * Round 31 D5: a SHORT post is a post whose title is NULL, not empty and not missing (a fixture or an
     * older payload without the field stays an ordinary post, with GROUP-5's stand-in above). It renders
     * title-less: its text is the post, so there is no heading, the whole-row link is named by that text,
     * and a signed-in viewer gets the inline reply bar (D10; since SV-147 the compact Thought composer). Design:
     * designs/00-inbox/2026-09-23-short-posts-and-idea-graph-design.md.
     */
    const isShortPost = discussion.title === null;
    const shortPostText = isShortPost ? (discussion.excerpt?.trim() ?? "") : "";
    // SV-147 D2: a title-less card's stamp is the bare relative time (the short-post convention, no new
    // key); a long card keeps "Started {time}".
    const startedLabel = !formattedCreated
        ? ""
        : isShortPost ? formattedCreated : localizer("discussion.started_at", formattedCreated);
    const displayTitle = isShortPost && shortPostText
        ? shortPostText
        : discussion.title?.trim() ? discussion.title : localizer("discussion.untitled");
    const displayAuthorUsername = getDisplayUsername(discussion.authorUsername, localizer);
    const authorProfilePath = getUserProfilePath(discussion.authorUsername, "discussions");
    const showUpdatedLabel = Boolean(updatedAt && formattedCreated !== formattedUpdated);

    const isGroup = isGroupDiscussion(discussion);
    const groupPublicId = isGroup ? discussion.groupPublicId ?? "" : "";
    const resolvedGroupPublicId = groupPublicId || contextGroupPublicId;
    // Group attribution for the cross-group "Your groups" home feed. Only that feed populates
    // groupName (single-group feeds leave it null), so the chip renders only there.
    const groupAttributionName = isGroup
        ? ((discussion as GroupDiscussionDTO & { groupName?: string | null }).groupName ?? null)
        : null;
    // Follow-payoff slice 2 (FP-2b, decision A17): the home feed says WHY a card is here, for the one
    // source a member chose by hand. Rendered only on the raw FOLLOWED_USERS source, never on the
    // MULTIPLE display label a near-tie collapses into. A statement, not an action: it earns a link to
    // the author and nothing else (rule 23; a per-row follow control shipped and was reverted on
    // 2026-08-30/31 for exactly this).
    const becauseYouFollowHref = (discussion as FeedCardSourceFields).sourceType === FOLLOWED_USERS_SOURCE_TYPE
        ? authorProfilePath
        : null;
    const initiallyExcluded = isGroup ? Boolean(discussion.isExcluded) : false;
    const excludeReason = isGroup ? (discussion.excludedReason ?? "") : "";
    const excludedByUserId = isGroup ? (discussion.excludedByUserId ?? null) : null;
    const excludedAt = isGroup ? (discussion.excludedAt ?? null) : null;
    const resolvedPinnedDiscussion = currentGroupPinnedDiscussion
        ?? (((discussion as PinnedGroupDiscussionLike).pinId != null || (discussion as PinnedGroupDiscussionLike).id != null)
            ? (discussion as PinnedGroupDiscussionLike)
            : undefined);
    const shouldAttemptCarousel = parsedCardMediaItems.length > 0;
    const allImagesFailed = shouldAttemptCarousel && Boolean(imageRenderability?.allImagesFailed);
    // Read the carousel's own `isResolving` rather than re-deriving it from `hasRenderableImages`.
    // The two disagree in exactly the case that matters: a card whose active slide has already failed
    // while other slides sit unattempted has nothing renderable AND nothing left to wait for, and the
    // old predicate showed the overlay forever. `?? true` keeps the pre-mount frame covered, which is
    // what `!undefined` did before. See the `isResolving` note in DiscussionCardMediaCarousel.
    const showLoadingSpinner = shouldAttemptCarousel
        && !allImagesFailed
        && (imageRenderability?.isResolving ?? true);
    const showCarousel = shouldAttemptCarousel && !allImagesFailed;
    const showExcerpt = Boolean(discussion.excerpt?.trim());
    const isSensitive = Boolean(discussionWithFlags.sensitive);
    const isSpoiler = Boolean(discussionWithFlags.spoiler);
    // `GROUP-78`: the phone thumbnail's source, only where the prop asks for it and it is safe to paint
    // outside the sensitive guard. An image slide paints its own source; a clip or an embed paints its
    // preview (a clip's source is the MP4, never paintable).
    const phoneThumbnailFirstSlide = parsedCardMediaItems[0];
    const phoneThumbnailUrl = phoneMediaLayout === "thumbnail" && showCarousel && !isShortPost && !isSensitive && !isSpoiler && phoneThumbnailFirstSlide
        ? (phoneThumbnailFirstSlide.kind === "image" ? phoneThumbnailFirstSlide.sourceUrl : (phoneThumbnailFirstSlide.previewImageUrl ?? null))
        : null;
    // U50 (`PROFILE-22`) stopped gating the owner's own profile page, and with no page gate nothing waives
    // masking for the cards on it, so the author was asked to reveal each of their own sensitive posts
    // (devo gate `957f2e2994`, 2026-09-28). The author is exempt at the card, like every other card guard.
    const viewerIsAuthor = Boolean(discussion.authorUsername && discussion.authorUsername === MetaHandler.getLoggedInUsername());
    const isArchived = Boolean(discussionWithLifecycleState.archived);
    const isDeleted = Boolean(discussionWithLifecycleState.deleted);
    // POLL facet, A33: a badge and nothing else on the card (no feed voting in v1). Read structurally
    // because `DiscussionWithoutContentDTO` carries no facet bit yet (2026-09-14; owed to the backend,
    // poll design §11.3.3): absent reads as "not a poll", so the deploy halves are order-independent
    // and the failure mode is a missing badge rather than a wrong one (the rule-34 shape).
    const isPoll = (discussion as { isPoll?: unknown }).isPoll === true;
    // Round 37 `2a` (FI-587, D1/D9): replies since the signed-in viewer last read this post. The home feed attaches
    // it from its page's `newReplyCounts` map; every other host leaves it absent, and absent renders nothing. Read
    // structurally for the same reason as `isPoll`. A read post is never dimmed: this chip is the only read-state cue.
    const newRepliesSinceLastVisit = (discussion as { newRepliesSinceLastVisit?: unknown }).newRepliesSinceLastVisit;
    const newReplyCount = typeof newRepliesSinceLastVisit === "number" && newRepliesSinceLastVisit > 0
        ? newRepliesSinceLastVisit
        : 0;
    const { sensitivePreference, hideSensitiveMedia } = resolveSensitiveMaskingConfig(getSensitivePreferenceMode(), sensitiveMasking);
    const discussionHref = buildSeriesDiscussionHref(discussion.slug ?? discussion.publicId, {
        contextGroupPublicId,
        contextSeriesPublicId,
    });
    const normalizedArgumentsCount = Number(discussion.argumentsCount ?? 0);
    // SV-56: make the need legible where it is cheap. A concrete, visible need gets met more often than
    // an abstract appeal (Kraut & Resnick), and the reply-need ranking term is what routes readers to
    // these posts in the first place — a lift with no visible cue asks the reader to notice a "0".
    //
    // The copy was `opening.no_replies` from 2026-08-03 to 2026-08-22, borrowed deliberately because it
    // was already authored in all 25 catalogs and a new key was a 25-locale bill for one cue.
    //
    // That borrowing is now SPENT (ux-critique DASHBOARD-31, operator decision D10, wave 8). The word
    // was the problem: the count chip renders "10,468 thoughts" and the section dropdown offers
    // "Thoughts", and this line said "No replies yet" about the SAME field, six inches apart on the
    // owner's own dashboard. A member had to work out whether replies and thoughts were the same
    // object, on the surface where the product's whole vocabulary rests on "thought" being the unit.
    // Spanish carried the split through unchanged.
    //
    // So the cue takes its own key and says thoughts, converging with the argument-to-thought rename
    // rather than fighting it. The chip and the section label already said thoughts and are unchanged.
    //
    // It rides the author/timestamp metadata line rather than the CardFooter deliberately. The footer's
    // height is RESERVED by ssr_home_first_card.jte and its row is already at the wrap edge on a ~390px
    // phone (~300px of 44px tap targets in ~334px), so a badge there would wrap the row and grow the
    // footer past its reservation — the exact defect
    // designs/01-now/2026-07-29-mobile-avatar-load-resilience-and-card-action-row-fit-design.md records.
    //
    // It keys off the TOTAL reply count, not off the ranking term's own predicate, and that is a
    // disclosure limit rather than a shortcut: the term's predicate is derived from the controlled-
    // account cohort, so a cue that fired on "no ORGANIC replies" would render this line beside a
    // comment count of five and tell every reader which accounts are controlled. Design
    // 2026-08-03-organic-reply-allocation-programme-design.md §5.1.4.
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive, isSpoiler }),
        [isSensitive, isSpoiler, localizer],
    );

    // The date the recovery list is actually organised around. The one line of metadata a deleted row
    // gets used to be spent on when the post was STARTED — the fact that does not help someone
    // recognise what they removed last Tuesday — while the deletion date appeared nowhere
    // (EMPTY-STATES-23, 2026-08-14). `deletedAt` is already on the DTO and already projected by the
    // list queries (`base.deleted_at`), so this needs no server change; it falls back to the started
    // stamp if a caller ever supplies `deleted` without it.
    const formattedDeleted = isDeleted && discussionWithLifecycleState.deletedAt
        ? df.timeSince(discussionWithLifecycleState.deletedAt)
        : "";
    const lifecycleTimestampLabel = formattedDeleted
        ? localizer("discussion.deleted_at", formattedDeleted)
        : startedLabel;
    const lifecycleTimestampValue = formattedDeleted
        ? (discussionWithLifecycleState.deletedAt ?? undefined)
        : createdAt;
    // Deleting a post sets `updated_at` to the deletion instant (DiscussionService), so leaving the
    // "Updated …" stamp in would print the same moment twice under a different name.
    const showUpdatedStamp = showUpdatedLabel && !formattedDeleted;
    // QA-18: the absolute date behind each relative stamp, as its <time title>. Memoized: a feed renders
    // many cards and `formatDateTime` builds an `Intl.DateTimeFormat` per call.
    const lifecycleTimestampAbsolute = React.useMemo(
        () => (lifecycleTimestampValue ? df.formatDateTime(lifecycleTimestampValue) : undefined),
        [df, lifecycleTimestampValue],
    );
    const updatedAtAbsolute = React.useMemo(
        () => (updatedAt ? df.formatDateTime(updatedAt) : undefined),
        [df, updatedAt],
    );

    // `EMPTY-STATES-78` — a deleted card is an OBJECT TO ACT ON, not a page to open.
    //
    // Every other card in the product is a link with its actions layered on top, and the full-bleed
    // overlay anchor at the bottom of this component is what makes that true. On the dashboard's
    // deleted sub-tab — the only list in the product that renders deleted cards, and the only screen
    // carrying `restoreOwnDeletedDiscussion` — that inverts the target sizes: the card is ≈450x476
    // CSS px on a 412px phone and the Restore pill beside it is 90x44, so the accidental target is
    // roughly fifty times the intended one.
    //
    // And the accident costs more than a miss. `/discussion/{slug}` for a deleted post is an HTTP 410
    // (`DiscussionConversationController.applyDeletedDiscussionPageMetadata`) rendering
    // `discussion.deleted.title` / `discussion.deleted.message` — copy written for a stranger, shown
    // to the author — with no restore path and, under an infinite-scroll list, no cheap way back to
    // the member's place. So the destination offers the author strictly less than the row they are
    // already looking at.
    //
    // Removing the anchor therefore removes no capability, only a mis-tap. It is the same call this
    // component's neighbours already made on the same state: Share went on `isDeleted`
    // (`EMPTY-STATES-24`), and `DiscussionButtons` guards edit, archive, delete, report and bookmark
    // the same way. Both doors go together — the hero carousel's tap resolves to the identical href,
    // so leaving it would keep the largest element on the card pointing at the tombstone.
    const cardOpensDiscussion = !isDeleted;

    // `viewsCount` is on the DTO for every list; `showReadCount` is what decides whether it is drawn.
    // A missing value renders nothing rather than a zero: a post whose count did not come back has an
    // UNKNOWN number of reads, and a member reads a zero as a fact (the same reasoning
    // `MyDashboardController.dashboardMapper` states for the dashboard counts).
    const rawViewsCount = (discussion as (DiscussionWithoutContentDTO | GroupDiscussionDTO) & {
        viewsCount?: number | null;
    }).viewsCount;
    const readCount = typeof rawViewsCount === "number" && Number.isFinite(rawViewsCount)
        ? rawViewsCount
        : undefined;
    // The label is the noun the post's own metrics page uses for the same number, reused rather than
    // minted (rule 11). It also carries the honest definition: "How often this post was opened" — a
    // read is an OPEN, not a person, and prod measures 2.13 opens per reader-post pair. Rendering the
    // number beside an icon rather than inside a sentence is what keeps this free of a plural form,
    // which across 25 catalogs is a defect source rather than a nicety.
    const readCountLabel = localizer("typescript.metrics.reads");
    // `bodyTextLength` is on `DiscussionWithoutContentDTO` for every feed that projects the column (the
    // group/pinned/picker shapes do not, and read as "unknown"). One predicate gates the token AND its
    // separator so a hidden label never leaves a dangling " · ".
    const bodyTextLength = (discussion as (DiscussionWithoutContentDTO | GroupDiscussionDTO) & {
        bodyTextLength?: number | null;
    }).bodyTextLength;
    const showReadingTime = hasReadingTime(bodyTextLength);

    // Round 7 slice 2b (D21, operator answer "Group post list"): the author's flair and contribution count in
    // THIS group. Only the group page's post list populates the two fields (design §9), so every other surface
    // renders nothing here by construction. Part of the byline only: it is a fact about the author, so it goes
    // when a list hides the author.
    const authorGroupFlair = isGroupDiscussion(discussion) ? discussion.authorGroupFlair : undefined;
    const authorGroupContributionsCount = isGroupDiscussion(discussion)
        ? discussion.authorGroupContributionsCount
        : undefined;
    const hasAuthorGroupFlair = Boolean(authorGroupFlair?.trim());
    const hasAuthorGroupContributions = typeof authorGroupContributionsCount === "number";
    // `GROUP-65` (U25, mega solo `mr2-group`): the standing is a fact about the AUTHOR, so it rides beside the
    // author's name (AuthorAttribution's `nameAdornment`), not at the end of the post's own metadata line,
    // where "5 contributions" read as this post's reply count against a footer counter showing 0.
    const authorGroupStanding = hasAuthorGroupFlair || hasAuthorGroupContributions ? (
        <span className="inline-flex flex-wrap items-center gap-x-1" data-playwright-id="discussion-card-author-standing">
            {hasAuthorGroupFlair ? (
                <GroupMemberFlairChip
                    flair={authorGroupFlair}
                    playwrightId="discussion-card-author-flair"
                    className="px-1.5 py-0 text-[11px]"
                />
            ) : null}
            {hasAuthorGroupContributions ? (
                <span className="text-xs text-muted-foreground" data-playwright-id="discussion-card-author-contributions">
                    {localizer("group.member.contributions", authorGroupContributionsCount)}
                </span>
            ) : null}
        </span>
    ) : null;

    // Extracted so the byline and the identity-free variant render the SAME timestamps rather than two
    // hand-kept copies of them, which is how the two would drift.
    const authorAttributionDetails = (
        <>
            {/* HOME-FEED-47: the relative-time phrase is ONE unit ("Started 29 minutes ago" never
                leaves "ago", or "months ago", alone on a line at 412px); the byline wraps at the
                ` · ` separators instead. */}
            {/* `DESIGN-SYSTEM-17` (V89 a): below `sm` a card with BOTH stamps shows only the latest
                activity. Name, "Started …" and "Updated …" wrapped to three lines beside the avatar on
                a phone, ahead of every title. The started stamp and its separator step aside there;
                from `sm` up, and on a card with one stamp, nothing changes. */}
            {lifecycleTimestampLabel ? <time className={showUpdatedStamp ? "whitespace-nowrap max-sm:hidden" : "whitespace-nowrap"} data-card-stamp="started" dateTime={lifecycleTimestampValue} title={lifecycleTimestampAbsolute}>{lifecycleTimestampLabel}</time> : null}
            {showUpdatedStamp ? (
                <>
                    {lifecycleTimestampLabel ? <span aria-hidden={true} className="max-sm:hidden">{" · "}</span> : null}
                    <time className="whitespace-nowrap" data-card-stamp="updated" dateTime={updatedAt} title={updatedAtAbsolute}>{localizer("discussion.updated_at", formattedUpdated)}</time>
                </>
            ) : null}
            {/* `HOME-FEED-16` / `GROUP-22` (wave-12 sheet Q10, option A), 2026-08-24 — the byline's
                "No thoughts yet" clause was DELETED here. The card was saying the same thing twice: this
                phrase, and the footer count button rendering `0` about 40 px below it. On a 412 px phone
                the clause is what pushed the byline to two lines, so dropping it buys back ~20 CSS px on
                most mobile cards — compounding with the footer's own fold (`DISCUSSION-30`).

                The numeral in the footer is the surviving statement, and it is the better one: it is
                already labelled by its control and it scales to every other count the card prints. The
                cue's `discussion-card-no-replies` hook went with it, and so did
                `tests/feed-card-reply-need-cue.spec.mjs`, whose whole subject was the cue —
                `test-impact-index.md` carries that retirement.

                This does NOT retire the reply-need RANKING term (SV-56). That is a backend scoring
                input with its own real-Postgres coverage; only its card-side cue is gone. */}
            {/* Reading time (round-6 `2a`, D5 / A4): from the DTO's own `bodyTextLength`, hidden under a
                minute, beside the read count. A list shape without the field renders nothing here. */}
            {showReadingTime ? (
                <>
                    {lifecycleTimestampLabel || showUpdatedStamp
                        ? <span aria-hidden={true}>{" · "}</span>
                        : null}
                    <ReadingTimeLabel localizer={localizer} bodyTextLength={bodyTextLength} />
                </>
            ) : null}
            {showReadCount && readCount !== undefined ? (
                <>
                    {lifecycleTimestampLabel || showUpdatedStamp || showReadingTime
                        ? <span aria-hidden={true}>{" · "}</span>
                        : null}
                    <span
                        data-playwright-id="discussion-card-read-count"
                        data-read-count={String(readCount)}
                        className="inline-flex items-center gap-1"
                        title={readCountLabel}
                    >
                        <Eye className="size-3.5 shrink-0" aria-hidden={true}/>
                        <span aria-label={readCountLabel}>{formatNumber(readCount)}</span>
                    </span>
                </>
            ) : null}
        </>
    );

    // Fired from the card's two real navigation paths — the full-card overlay link and a tap on the
    // media carousel — and from nowhere else. Deliberately NOT an onClickCapture on the card container:
    // that would also count a vote, a bookmark toggle or an author-link click as a feed card click.
    // SV-121 M2.3 (round 33 slice 3a): the list threads only the displayed label, so the card adds its own
    // RAW primary source (`sourceType`, set by the home feed only) for the `feed_click` record.
    const primarySource = (discussion as FeedCardSourceFields).sourceType;
    const handleFeedCardActivated = React.useCallback(() => {
        if (!feedAttribution) {
            return;
        }
        onFeedCardActivated(
            primarySource ? {...feedAttribution, primarySource} : feedAttribution,
            discussion.publicId,
        );
    }, [discussion.publicId, feedAttribution, primarySource]);

    // The board, if this post has one (design §18.2). Batched across every card on the page by
    // `boardCardSummaries`, so this is one request per feed page rather than one per card, and it
    // resolves to null for the great majority of posts that are not boards.
    const boardSummary = useBoardCardSummary(discussion.publicId);

    // BOARD-17 (D16): the lane's Skip, recorded server-side. The card removes itself rather than asking the
    // list to re-page: the lane query already excludes it on the next load, and until then an empty slot
    // is the honest answer to "take this out of my lane".
    const [passedOutOfLane, setPassedOutOfLane] = React.useState(false);
    const onBoardPassed = React.useCallback(() => setPassedOutOfLane(true), []);

    // SV-135 S2 (design §5): the gutter avatar is the author link's visual, so it is an anchor with the
    // member's name as its accessible name, raised above the whole-row link exactly as the name is.
    // A member with no profile route (an erased account) gets the same avatar, unlinked.
    const gutterAvatarImage = (
        <ProfileImage
            localizer={localizer}
            imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
            username={discussion.authorUsername ?? displayAuthorUsername}
            mode="user"
            hoverable={Boolean(authorProfilePath)}
            className="size-10"
        />
    );
    const gutterAvatar = !showAuthor ? null : authorProfilePath ? (
        <a
            href={authorProfilePath}
            className={FEED_ROW_GUTTER_AVATAR_CLASS}
            data-playwright-id="discussion-card-avatar-gutter"
            translate="no"
            aria-label={displayAuthorUsername}
        >
            {gutterAvatarImage}
        </a>
    ) : (
        <span className={FEED_ROW_GUTTER_AVATAR_CLASS} data-playwright-id="discussion-card-avatar-gutter">
            {gutterAvatarImage}
        </span>
    );

    if (passedOutOfLane) {
        return null;
    }

    // `GROUP-78`: the title row gains the phone thumbnail only when one will paint; every other card keeps
    // the heading as a direct child exactly as before (no wrapper element at all).
    const wrapPhoneThumbnailTitleRow = (title: React.ReactNode) => (phoneThumbnailUrl ? (
        <div className="flex min-w-0 items-start gap-3" data-playwright-id="discussion-card-title-row">
            {title}
            {/* Decorative (`alt=""`: the heading beside it names the post) and under the whole-row link, so
                a tap on it opens the post exactly as the full-bleed image did. `sm:hidden`: the hero
                returns from `sm` up. */}
            <div className="ms-auto mt-0.5 shrink-0 sm:hidden" data-playwright-id="discussion-card-inline-thumbnail">
                <SafeImage
                    src={phoneThumbnailUrl}
                    alt=""
                    seavienImageSize={THUMBNAIL_IMAGE_SIZE}
                    width={64}
                    height={64}
                    className="size-16 rounded-lg border border-border object-cover"
                />
            </div>
        </div>
    ) : title);

    return (
        <div
            data-slot="card"
            data-playwright-id="discussion-card"
            // The card's own link is built from `discussion.slug ?? publicId`, so a published post's
            // href carries the SLUG and never the public id. A test identifying a card by
            // `[href*="<publicId>"]` therefore matches nothing, silently — which is exactly how
            // `dashboard-deleted-content-restore` came to assert a restore that had genuinely worked
            // (2026-08-13). This is the stable hook rule 20 asks for; identify cards by it.
            data-public-id={discussion.publicId}
            data-short-post={isShortPost ? "true" : undefined}
            className={FEED_CARD_ROOT_CLASS}>
            {/* One grid for the whole row: the avatar gutter, then ONE text column holding the author
                line, title, excerpt, media and actions in reading order (D1). A list that hides the
                author (`showAuthor={false}`) has no gutter to align to, so it drops the column. */}
            <div className={showAuthor
                ? (isShortPost ? FEED_ROW_GRID_COMPACT_CLASS : FEED_ROW_GRID_CLASS)
                : (isShortPost ? "px-4 pt-3 pb-2" : "px-4 py-3")}>
                {gutterAvatar}
                <div className="min-w-0">
                    <div data-slot="card-description" className="min-w-0 text-muted-foreground">
                        {/* The timestamps survive `showAuthor={false}`; only the avatar and handle go. The
                            identity-free variant is rendered OUTSIDE AuthorAttribution because that
                            component's row is the subject of the first-line alignment guard
                            (card-author-attribution-alignment.spec.mjs). The row's own avatar is off
                            here: the gutter above draws it. */}
                        {/* D2 (round 31 `3b`): the author line ends in the `…` menu's slot, top right. */}
                        <div className="flex min-w-0 items-start gap-2">
                            <div className="min-w-0 flex-1">
                                {showAuthor ? (
                                    <AuthorAttribution localizer={localizer}
                                        name={displayAuthorUsername}
                                        href={authorProfilePath}
                                        imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
                                        avatarSeed={discussion.authorUsername ?? displayAuthorUsername}
                                        nameAdornment={authorGroupStanding ?? undefined}
                                        details={authorAttributionDetails}
                                        showAvatar={false}
                                        className="text-[15px]"
                                        nameClassName="font-semibold text-foreground"
                                    />
                                ) : (
                                    <span className="flex min-w-0 flex-wrap items-center gap-x-1 text-sm text-muted-foreground">
                                        {authorAttributionDetails}
                                    </span>
                                )}
                            </div>
                            <span ref={menuSlotRef} className={FEED_ROW_MENU_SLOT_CLASS} data-playwright-id="discussion-card-menu-slot"/>
                        </div>
                        {groupAttributionName ? (
                            <span
                                className="mt-1 inline-flex w-fit max-w-full items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-neutral-text"
                                data-playwright-id="home-groups-feed-attribution"
                            >
                                <Users className="size-3 shrink-0" aria-hidden={true}/>
                                <span className="truncate">{groupAttributionName}</span>
                            </span>
                        ) : null}
                        {becauseYouFollowHref ? (
                            // Same pill as the group chip (flat-X: border, muted wash, no shadow), as a LINK
                            // that outranks the full-card overlay exactly as the author link does. FI-808: and
                            // with the author link's coarse-pointer floor (`touch-hit-44`) and the one focus ring.
                            <a
                                href={becauseYouFollowHref}
                                className="relative z-layer-raised-front touch-hit-44 mt-1 inline-flex w-fit max-w-full items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-neutral-text hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                data-playwright-id="feed-card-because-you-follow"
                            >
                                <UserRound className="size-3 shrink-0" aria-hidden={true}/>
                                <span className="truncate">{localizeWithMemberName(localizer, "feed.card.because_you_follow", displayAuthorUsername)}</span>
                            </a>
                        ) : null}
                        {newReplyCount > 0 ? (
                            // Round 37 `2a`: the group chip's pill, inert (the whole-card link opens the post).
                            <span
                                className="mt-1 inline-flex w-fit max-w-full items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-neutral-text"
                                data-playwright-id="discussion-card-new-replies"
                                data-new-reply-count={String(newReplyCount)}
                            >
                                {localizer("feed.card.new_replies", newReplyCount)}
                            </span>
                        ) : null}
                    </div>

                    {/* `headingLevel={3}` is the ONE choice for every feed host — this component backs the
                        home feed, the group feed, search, profile lists, series lists and the dashboard, and
                        GROUP-4's constraint is that the level be picked once rather than per surface, or the
                        heading tree disagrees between two pages showing the same card. Nothing is
                        subtracted by the mount (rule 32): the crawlable SSR list emits `<li>` + link per
                        record with no heading at all, so the island strictly adds one. */}
                    {isShortPost ? (
                        // Title-less (D5): no heading, and no empty title band. The state badges a titled
                        // post carries inside its heading keep their place as a plain line, only when
                        // one applies.
                        (isArchived || isDeleted || isSensitive || isSpoiler) ? (
                            <div className="min-w-0 mt-0.5" data-playwright-id="discussion-card-short-post-badges">
                                {isArchived ? (
                                    <span className="me-2 inline-flex items-center rounded-full border border-border/60 bg-muted/70 px-2 py-0.5 align-middle text-xs font-medium text-muted-foreground">
                                        {localizer("discussion.archived.badge")}
                                    </span>
                                ) : null}
                                {isDeleted ? (
                                    <span
                                        className="me-2 inline-flex items-center rounded-full border border-border/60 bg-muted/70 px-2 py-0.5 align-middle text-xs font-medium text-muted-foreground"
                                        data-playwright-id="discussion-card-deleted-badge"
                                    >
                                        {localizer("discussion.deleted.title")}
                                    </span>
                                ) : null}
                                {isSensitive && <SensitiveBadge className="me-2 py-0 align-middle">{localizer("create.discussion.sensitive.label")}</SensitiveBadge>}
                                {isSpoiler && <SensitiveBadge className="me-2 py-0 align-middle">{localizer("content-warning.badge.spoiler")}</SensitiveBadge>}
                            </div>
                        ) : null
                    ) : wrapPhoneThumbnailTitleRow(
                    <CardTitle headingLevel={3} className="min-w-0 mt-0.5 text-[17px] font-bold leading-snug text-foreground">
                        {/* HOME-FEED-49 (U70): a 4-line clamp, so a title near the 100-character limit
                            takes 4 lines on a phone instead of 9-10. CSS-only, so the full title still
                            reaches the heading outline and the accessible name. Mirrored in
                            `ssr_home_first_card.jte` so the SSR card reserves the same height. */}
                        <span className="line-clamp-4 min-w-0 wrap-anywhere">
                            {/* The series sequence number, FIRST on the identity line and inside the
                                heading (`SERIES-1`, `SERIES-4`, wave 8 2026-08-22). It used to sit in
                                `CardFooter` beside `DiscussionButtons`, which does not fit on one line at
                                393px: the footer wrapped to a 26px pill above 319px of nothing, costing a
                                measured +32.7 CSS px per card and ~262 CSS px on an 8-card series page,
                                with a 37px dead gutter left of the action cluster.

                                This is the SAME decision the no-replies cue below carries, and the same
                                one `seriesSequenceCue` already carried — the footer row is at the wrap
                                edge on a ~390px phone and `ssr_home_first_card.jte` RESERVES its height,
                                so nothing conditional may be added to it. The sequence pill was the one
                                series signal still there and it put the row back over the budget
                                2026-07-29-mobile-avatar-load-resilience-and-card-action-row-fit-design.md
                                §2 set.

                                Reading it first is also what `SERIES-4` asked for: sequence becomes the
                                first thing read in each row rather than a corner pill under six social
                                controls, and a screen reader navigating by heading hears "4, <title>"
                                instead of eight indistinguishable titles. The no-pill case is untouched —
                                the footer renders exactly what every other feed renders. */}
                            {seriesSequenceNumber ? (
                                <span
                                    data-playwright-id="discussion-card-series-sequence-number"
                                    className="me-2 inline-flex min-w-[1.65rem] shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted px-1.5 py-0.5 align-middle text-xs font-semibold text-foreground"
                                    title={String(seriesSequenceNumber)}
                                >
                                    {seriesSequenceNumber}
                                </span>
                            ) : null}
                            {displayTitle}
                            {seriesSequenceCue ? (
                                <span className="ms-2 inline-flex items-center rounded-full border border-primary/20 bg-primary/8 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                                    {seriesSequenceCue}
                                </span>
                            ) : null}
                            {isArchived ? (
                                <span className="ms-2 inline-flex items-center rounded-full border border-border/60 bg-muted/70 px-2 py-0.5 align-middle text-xs font-medium text-muted-foreground">
                                    {localizer("discussion.archived.badge")}
                                </span>
                            ) : null}
                            {/* A deleted post used to render in EXACTLY the live feed card shell — same
                                border, fill, hero image, title weight, byline, excerpt and footer row — so
                                the only per-card signal that a whole list was deleted content was a
                                detached Restore pill (EMPTY-STATES-7, 2026-08-14). Believing deleted posts
                                are live and believing live posts are deleted both lead to the wrong next
                                action, including pressing the wrong Restore. Same pill shape as the
                                sibling archived state, reusing the existing `discussion.deleted.title` —
                                no new key. */}
                            {isDeleted ? (
                                <span
                                    className="ms-2 inline-flex items-center rounded-full border border-border/60 bg-muted/70 px-2 py-0.5 align-middle text-xs font-medium text-muted-foreground"
                                    data-playwright-id="discussion-card-deleted-badge"
                                >
                                    {localizer("discussion.deleted.title")}
                                </span>
                            ) : null}
                            {isSensitive && <SensitiveBadge className="ms-2 py-0 align-middle">{localizer("create.discussion.sensitive.label")}</SensitiveBadge>}
                            {isSpoiler && <SensitiveBadge className="ms-2 py-0 align-middle">{localizer("content-warning.badge.spoiler")}</SensitiveBadge>}
                            {/* The same pill the post page's question badge reuses; the shared appearance
                                is the point, not the sensitivity semantics. */}
                            {isPoll ? (
                                <SensitiveBadge className="ms-2 py-0 align-middle" dataPlaywrightId="discussion-card-poll-badge">
                                    {localizer("discussion.poll.badge")}
                                </SensitiveBadge>
                            ) : null}
                        </span>
                    </CardTitle>
                    )}

                    <SensitiveMediaGuard
                        isSensitive={isSensitive}
                        isSpoiler={isSpoiler}
                        hideSensitiveMedia={hideSensitiveMedia}
                        sensitivePreference={sensitivePreference}
                        isViewerOwner={viewerIsAuthor}
                        variant="card"
                        // The full-card link below is a sibling of this guard, so without this the masked scrim
                        // covers it and a blurred card cannot be opened without revealing first. Design §8.6.
                        overlayStacking="below-link-overlay"
                        className="flex min-h-0 flex-1 flex-col"
                        badgeLabel={warningCopy.badgeLabel}
                        label={warningCopy.label}
                        helperText={warningCopy.helperText}
                        revealText={warningCopy.revealText}
                        // The HIDE branch has no reveal control by design, so without this a masked card is a
                        // wall: nothing to press, and no way to reach the preference that produced it. An
                        // anonymous visitor is served `content="HIDE"`, so that wall was the DEFAULT
                        // logged-out experience of a sensitive card. The guard renders this only under HIDE
                        // (`!allowsLocalReveal`), so a blurred card is unchanged. `GROUP-1`, 2026-08-14.
                        preferencesRouteText={warningCopy.preferencesRouteText}
                    >
                        {showExcerpt && (
                            <div data-slot="card-content" className="min-w-0 mt-1">
                                {/* A short post's text IS the post, now up to 5,000 visible characters
                                    (SV-136 D5), so it keeps its line breaks and clamps at six lines rather
                                    than a long post's three (A3); the card stays a plain preview and the
                                    whole-row link opens the full post (D8). `ssr_home_first_card.jte` must
                                    carry the SAME classes: the island row replaces the SSR row on mount
                                    (rule 32), so a mismatch is a visible jump. */}
                                <p className={`discussion-card-text wrap-anywhere mb-0 text-[15px] leading-5 text-foreground${isShortPost ? " line-clamp-6 whitespace-pre-line" : " line-clamp-3"}`}>
                                    {discussion.excerpt}
                                </p>
                            </div>
                        )}

                        {/* ⚠️ The media block's raised layer below is CONDITIONAL, and that is the whole fix.
                            It may outrank the card's full-card link ONLY while the carousel actually pages: a
                            raised media block takes the pointer, the anchor beneath never sees it, and no
                            `click` event is dispatched at all (rule 26). When the carousel cannot page there is
                            nothing to page, so the card is a real link over its image again and Cmd/Ctrl-click
                            and middle-click work. `mediaPages` is REPORTED by the carousel, never counted here.
                            Guarded by the `elementFromPoint` assertion in
                            `discussion-card-link-reachability.spec.mjs` — a spec asserting that the card merely
                            NAVIGATES cannot see this, because the carousel's own `onTap` satisfies it while the
                            link is unreachable. */}
                        {showCarousel && (
                            <div
                                data-playwright-id="discussion-card-media"
                                data-card-media-pages={String(mediaPages)}
                                data-phone-media-layout={phoneThumbnailUrl ? "thumbnail" : undefined}
                                className={`relative mt-3 pb-0${mediaPages ? " z-layer-raised-front" : ""}${phoneThumbnailUrl ? " max-sm:hidden" : ""}`}
                            >
                                {/* The reserved media box. It is mirrored byte-for-byte by
                                    ssr_home_first_card.jte and its tokens live in css/00-variables.css —
                                    change all three together (feedCardBoxParity.spec.ts pins them).

                                    Aspect-honest as of 2026-08-01: when the first slide's intrinsic
                                    dimensions are known the box takes the clamped ratio inline and the
                                    --aspect modifier drops the fixed height; when they are not — permanent
                                    for embeds, external images and pre-2026-07-30 uploads — it stays exactly
                                    today's --feed-card-media-reserved-height box. The image inside goes on
                                    letterboxing with object-contain either way; note that passing a ratio to
                                    SafeImage instead would fight its max-h-full, which is why the BOX owns
                                    this and not the <img> (CLS design, probe B5). */}
                                <div
                                    className={`relative ${FEED_CARD_MEDIA_BOX_CLASS}${mediaBoxAspectRatio ? ` ${FEED_CARD_MEDIA_BOX_ASPECT_CLASS}` : ""}`}
                                    style={mediaBoxAspectRatio
                                        ? ({ [FEED_CARD_MEDIA_ASPECT_PROPERTY]: mediaBoxAspectRatio } as React.CSSProperties)
                                        : undefined}
                                >
                                    {showLoadingSpinner && (
                                        <div className="absolute inset-0 z-layer-raised-front flex items-center justify-center rounded-xl bg-background2/90 backdrop-blur-sm">
                                            <div className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-background1/90 px-5 py-4 text-center shadow-sm">
                                                <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden={true}/>
                                                <span className="sr-only">{localizer("image.list.loading")}</span>
                                            </div>
                                        </div>
                                    )}

                                    <DiscussionCardMediaCarousel
                                        items={parsedCardMediaItems}
                                        alt={discussion.title ?? ""}
                                        localizer={localizer}
                                        onTap={cardOpensDiscussion ? () => {
                                            handleFeedCardActivated();
                                            navigateToUrl(discussionHref, {
                                                source: "discussion-card-image",
                                            });
                                        } : undefined}
                                        seavienImageSize={SMALL_IMAGE_SIZE}
                                        seavienSrcSetSizes={RESPONSIVE_CARD_IMAGE_SIZES}
                                        imageSizes="(max-width: 640px) 100vw, 600px"
                                        priorityFirstSlide={priorityImage}
                                        className={showLoadingSpinner ? "mx-auto w-full invisible pointer-events-none" : "mx-auto w-full"}
                                        slideHeightClassName={FEED_CARD_MEDIA_SLIDE_CLASS}
                                        onRenderabilityChange={setImageRenderability}
                                        onPagingChange={setMediaPages}
                                    />
                                </div>
                            </div>
                        )}

                        {boardSummary ? (
                            <BoardCardBlockLazy
                                localizer={localizer}
                                discussionPublicId={discussion.publicId ?? ""}
                                discussionHref={discussionHref}
                                summary={boardSummary}
                                onPassed={boardLaneSkippable ? onBoardPassed : undefined}
                            />
                        ) : null}

                        {/* The action row, in the text column under the media. `relative
                            z-layer-raised-front` so its controls outrank the whole-row link. The muted,
                            spread `layout="row"` (S3); its `…` menu is portaled into the author line's
                            slot above (D2, round 31 `3b`). */}
                        <div
                            data-slot="card-footer"
                            className={`relative z-layer-raised-front ${FEED_ROW_ACTIONS_CLASS}`}
                        >
                            <DiscussionButtons
                                layout="row"
                                menuSlot={menuSlotRef}
                                discussionPublicId={discussion.publicId ?? ""}
                                discussionSlug={discussion.slug ?? undefined}
                                initialAgreementsCount={Number(discussion.agreementsCount ?? 0)}
                                initialDisagreementsCount={Number(discussion.disagreementsCount ?? 0)}
                                initialAddedToSeriesCount={Number(discussion.discussionAddedToSeriesCount ?? 0)}
                                initialArgumentsCount={normalizedArgumentsCount}
                                initiallyAgreeing={Boolean(discussion.isUserAgreeing)}
                                initiallyDisagreeing={Boolean(discussion.isUserDisagreeing)}
                                initiallyAddedToAnySeries={Boolean(discussion.isAddedToAnySeries)}
                                initiallySavedToBookmarks={Boolean(discussionWithBookmarksState.isAddedToBookmarksSeries)}
                                // A short post's title is null (D5): its share payload is named by the post's own
                                // text, the value that already names this row. The share chokepoint bounds it
                                // (`PlatformIdentifierUtil`: 100-char title, 200-char word-boundary text).
                                discussionTitle={isShortPost ? (shortPostText || undefined) : discussion.title}
                                authorDisplayName={displayAuthorUsername}
                                authorUsername={discussion.authorUsername}
                                isPrivateDiscussion={Boolean(discussion.privateAccess)}
                                isArchived={isArchived}
                                isDeleted={isDeleted}
                                localizer={localizer}
                                canEdit={Boolean(discussion.authorUsername && discussion.authorUsername === MetaHandler.getLoggedInUsername())}
                                initiallyExcluded={initiallyExcluded}
                                excludeReason={excludeReason}
                                excludedByUserId={excludedByUserId}
                                excludedAt={excludedAt}
                                groupPublicId={resolvedGroupPublicId}
                                showViewerReactions={showViewerReactions}
                                viewerSavedList={viewerSavedList}
                                initiallyViewerIsLeader={Boolean(viewerIsLeaderOfGroup)}
                                initiallyPinnedInGroup={Boolean(resolvedPinnedDiscussion)}
                                initialPinnedDiscussionPinId={resolvedPinnedDiscussion?.pinId ?? resolvedPinnedDiscussion?.id ?? null}
                                onPinnedInGroup={onGroupPinnedDiscussionAdded}
                                onUnpinnedFromGroup={onGroupPinnedDiscussionRemoved}
                                // SV-147 A3: no `isShortPost` here. A short post takes thoughts, so its card
                                // shows the thoughts count exactly as a long card does.
                            />
                        </div>

                        {/* D10 / SV-147 A4: under each short post (D3: short cards only), the compact
                            Thought composer publishing a top-level thought. Signed-in viewers only (an
                            anonymous reply goes through the post page's login offer, A5), never on a
                            deleted or archived post, and raised like the action row so typing in it never
                            activates the whole-row link. */}
                        {isShortPost && !isDeleted && !isArchived && discussion.publicId && MetaHandler.getLoggedInUsername() ? (
                            <div className="relative z-layer-raised-front">
                                <ShortPostReplyBox
                                    discussionPublicId={discussion.publicId}
                                    localizer={localizer}
                                />
                            </div>
                        ) : null}
                    </SensitiveMediaGuard>
                </div>
            </div>

            {/* `focus-visible` treatment with a NEGATIVE outline offset (`EMPTY-STATES-41`). This anchor
                is the only way to open the row with a keyboard and carried no focus treatment at all,
                while the visually identical search rows carry the app's token ring. The negative offset
                keeps the ring inside the row: an outline at offset 0 paints outside the border box, over the
                neighbouring row's divider (and, before SV-135 S2, under the old root's clip). One
                string across all five card families — see `ThoughtCardDisplayer`. */}
            {cardOpensDiscussion ? (
                <a href={discussionHref} data-playwright-id="discussion-card-link"
                   className="absolute inset-0 z-layer-base-front focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                   onClick={handleFeedCardActivated}
                   aria-label={displayTitle}></a>
            ) : null}
        </div>
    );
}
